// components/questions/BulkUploadModal.tsx

import React, { useState } from "react";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import { toast } from "react-hot-toast";
import * as XLSX from 'xlsx';

interface BulkUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (questions: any[]) => Promise<void>;
  bankId: string;
  bankName: string;
  questionTypes: any[];
  traits?: any[];
  scales?: any[];
}

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  bankId,
  bankName,
  questionTypes,
  traits = [],
  scales = []
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [previewData, setPreviewData] = useState<any[]>([]);

  const downloadTemplate = () => {
    const aptitudeTemplate = {
      "Question Text": "Sample aptitude question?",
      "Option A": "Option A text",
      "Option B": "Option B text",
      "Option C": "Option C text",
      "Option D": "Option D text",
      "Correct Answer": "A",
      "Question Type": "Multiple Choice",
      "Difficulty": "Medium",
      "Marks": 1,
      "Negative Marks": 0,
      "Time Limit (seconds)": 60,
      "Explanation": "Explanation for the correct answer",
      "Tags": "tag1, tag2, tag3"
    };

    const psychometricTemplate = {
      "Question Text": "I enjoy working in teams",
      "Option A": "Strongly Disagree",
      "Option B": "Disagree",
      "Option C": "Neutral",
      "Option D": "Agree",
      "Option E": "Strongly Agree",
      "Question Type": "Psychometric Scale",
      "Difficulty": "Medium",
      "Marks": 0,
      "Negative Marks": 0,
      "Time Limit (seconds)": 30,
      "Explanation": "This measures teamwork orientation",
      "Tags": "teamwork, collaboration",
      "Trait Name": "Teamwork",
      "Scale Name": "Likert 5-Point",
      "Is Reverse Scored": "FALSE"
    };

    const ws1 = XLSX.utils.json_to_sheet([aptitudeTemplate]);
    const ws2 = XLSX.utils.json_to_sheet([psychometricTemplate]);
    const wb = XLSX.utils.book_new();
    
    XLSX.utils.book_append_sheet(wb, ws1, "Aptitude Questions");
    XLSX.utils.book_append_sheet(wb, ws2, "Psychometric Questions");
    
    XLSX.writeFile(wb, `question_bank_template_${bankName}.xlsx`);
    toast.success("Template downloaded successfully");
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.match(/\.(xlsx|xls)$/)) {
      toast.error("Please upload an Excel file (.xlsx or .xls)");
      return;
    }
    
    setFile(file);
    
    const reader = new FileReader();
    reader.onload = (evt) => {
      const data = new Uint8Array(evt.target?.result as ArrayBuffer);
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(firstSheet);
      setPreviewData(jsonData.slice(0, 5));
    };
    reader.readAsArrayBuffer(file);
  };

  const getTraitIdByName = (traitName: string): string | null => {
    const trait = traits.find(t => 
      t.name.toLowerCase() === traitName?.toLowerCase()
    );
    return trait?.id || null;
  };

  const getScaleIdByName = (scaleName: string): string | null => {
    const scale = scales.find(s => 
      s.scaleName?.toLowerCase() === scaleName?.toLowerCase()
    );
    return scale?.id || null;
  };

  const getScaleDetailsById = (scaleId: string): any => {
    return scales.find(s => s.id === scaleId);
  };

  const getQuestionTypeIdByCode = (typeName: string): string | null => {
    const questionType = questionTypes.find(qt => 
      qt.name.toLowerCase() === typeName?.toLowerCase() ||
      qt.code.toLowerCase() === typeName?.toLowerCase()
    );
    return questionType?.id || null;
  };

  const processAndUpload = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }

    setUploading(true);
    
    try {
      const reader = new FileReader();
      reader.onload = async (evt) => {
        const data = new Uint8Array(evt.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);
        
        const questions = [];
        const errors = [];
        
        for (let i = 0; i < jsonData.length; i++) {
          const row: any = jsonData[i];
          
          try {
            const questionTypeName = row["Question Type"];
            const questionTypeId = getQuestionTypeIdByCode(questionTypeName);
            
            if (!questionTypeId) {
              errors.push(`Row ${i + 2}: Invalid Question Type "${questionTypeName}"`);
              continue;
            }
            
            const questionType = questionTypes.find(qt => qt.id === questionTypeId);
            const isPsychometric = questionType?.code?.includes('psychometric') || 
                                   questionTypeName?.toLowerCase().includes('psychometric');
            
            const difficultyMap: any = {
              'easy': 'easy',
              'medium': 'medium',
              'hard': 'hard'
            };
            
            const difficulty = difficultyMap[row["Difficulty"]?.toLowerCase()];
            if (!difficulty) {
              errors.push(`Row ${i + 2}: Invalid Difficulty. Use Easy, Medium, or Hard`);
              continue;
            }
            
            let questionData: any = {
              text: row["Question Text"],
              bankId: bankId,
              type: questionTypeId,
              category: isPsychometric ? 'behavioral' : 'aptitude',
              difficulty: difficulty,
              points: parseFloat(row["Marks"]) || (isPsychometric ? 0 : 1),
              negativeMarks: parseFloat(row["Negative Marks"]) || 0,
              timeLimit: parseInt(row["Time Limit (seconds)"]) || 0,
              explanation: row["Explanation"] || "",
              tags: row["Tags"] ? row["Tags"].split(',').map((tag: string) => tag.trim()) : [],
              status: 'active',
              order: i,
              sectionId: "",
              language: "english",
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              createdBy: "bulk_upload"
            };
            
            if (isPsychometric) {
              const traitName = row["Trait Name"];
              const scaleName = row["Scale Name"];
              
              if (!traitName) {
                errors.push(`Row ${i + 2}: Trait Name is required for psychometric questions`);
                continue;
              }
              
              if (!scaleName) {
                errors.push(`Row ${i + 2}: Scale Name is required for psychometric questions`);
                continue;
              }
              
              const traitId = getTraitIdByName(traitName);
              const scaleId = getScaleIdByName(scaleName);
              
              if (!traitId) {
                errors.push(`Row ${i + 2}: Trait "${traitName}" not found in database`);
                continue;
              }
              
              if (!scaleId) {
                errors.push(`Row ${i + 2}: Scale "${scaleName}" not found in database`);
                continue;
              }
              
              const scaleDetails = getScaleDetailsById(scaleId);
              
              const options = [];
              const optionKeys = Object.keys(row).filter(k => k.startsWith("Option") && row[k]);
              
              for (let optIdx = 0; optIdx < optionKeys.length && optIdx < (scaleDetails?.numberOfPoints || 5); optIdx++) {
                const optValue = row[`Option ${String.fromCharCode(65 + optIdx)}`];
                if (optValue) {
                  const pointValue = scaleDetails?.pointLabels?.find((p: { point: number; value: number }) => p.point === optIdx + 1)?.value || optIdx + 1;
                  options.push({
                    id: `opt_${Date.now()}_${optIdx}`,
                    text: optValue,
                    label: String.fromCharCode(65 + optIdx),
                    value: pointValue
                  });
                }
              }
              
              questionData = {
                ...questionData,
                traitId: traitId,
                traitName: traitName,
                scaleId: scaleId,
                scaleType: scaleDetails?.scaleType || "likert",
                options: options,
                isReverse: row["Is Reverse Scored"]?.toLowerCase() === 'true' || row["Is Reverse Scored"] === 'TRUE',
                weightage: 1,
                maxScale: options.length
              };
            } else {
              const options = [];
              for (let optIdx = 0; optIdx < 4; optIdx++) {
                const optValue = row[`Option ${String.fromCharCode(65 + optIdx)}`];
                if (optValue) {
                  options.push({
                    id: `opt_${Date.now()}_${optIdx}`,
                    text: optValue,
                    label: String.fromCharCode(65 + optIdx),
                    isCorrect: row["Correct Answer"] === String.fromCharCode(65 + optIdx)
                  });
                }
              }
              
              const correctAnswer = row["Correct Answer"];
              if (!correctAnswer) {
                errors.push(`Row ${i + 2}: Correct Answer is required for aptitude questions`);
                continue;
              }
              
              if (options.length === 0) {
                errors.push(`Row ${i + 2}: At least one option is required`);
                continue;
              }
              
              questionData = {
                ...questionData,
                options: options
              };
            }
            
            questions.push(questionData);
          } catch (error) {
            errors.push(`Row ${i + 2}: Processing error - ${error}`);
          }
        }
        
        if (errors.length > 0) {
          let errorMessage = `Found ${errors.length} errors.\n`;
          errorMessage += errors.slice(0, 5).join('\n');
          if (errors.length > 5) {
            errorMessage += `\n... and ${errors.length - 5} more errors`;
          }
          toast.error(errorMessage);
          console.error("Upload errors:", errors);
          setUploading(false);
          return;
        }
        
        if (questions.length === 0) {
          toast.error("No valid questions found in the file");
          setUploading(false);
          return;
        }
        
        await onUpload(questions);
        setFile(null);
        setPreviewData([]);
        onClose();
      };
      
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error("Error processing file:", error);
      toast.error("Failed to process file");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Bulk Upload Questions - ${bankName}`}
      size="xl"
    >
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">Instructions:</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Download the template Excel file first</li>
            <li>• Use "Aptitude Questions" sheet for MCQ questions</li>
            <li>• Use "Psychometric Questions" sheet for scale-based questions</li>
            <li>• For psychometric questions: Trait Name and Scale Name must exactly match existing records in database</li>
            <li>• Available Scales: {scales.map(s => s.scaleName).join(', ')}</li>
            <li>• Available Traits: {traits.map(t => t.name).join(', ')}</li>
            <li>• Difficulty Level must be: Easy, Medium, or Hard</li>
            <li>• Maximum 500 questions per upload</li>
          </ul>
        </div>

        <div className="flex gap-3">
          <Button
            variant="primary"
            onClick={downloadTemplate}
            className="bg-green-600 hover:bg-green-700"
          >
            📥 Download Template
          </Button>
        </div>

        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileUpload}
            className="w-full"
          />
          {file && (
            <div className="mt-3 text-sm text-gray-600">
              Selected file: {file.name}
            </div>
          )}
        </div>

        {previewData.length > 0 && (
          <div>
            <h4 className="font-medium text-gray-800 mb-2">Preview (First 5 rows):</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border p-2 text-left">Question</th>
                    <th className="border p-2 text-left">Type</th>
                    <th className="border p-2 text-left">Difficulty</th>
                    <th className="border p-2 text-left">Trait/Scale</th>
                    <th className="border p-2 text-left">Marks</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((row, idx) => (
                    <tr key={idx}>
                      <td className="border p-2">{(row as any)["Question Text"]?.substring(0, 50)}...</td>
                      <td className="border p-2">{(row as any)["Question Type"]}</td>
                      <td className="border p-2">{(row as any)["Difficulty"]}</td>
                      <td className="border p-2">
                        {(row as any)["Trait Name"] ? `${(row as any)["Trait Name"]} / ${(row as any)["Scale Name"]}` : '-'}
                      </td>
                      <td className="border p-2">{(row as any)["Marks"]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="secondary" onClick={onClose} disabled={uploading}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={processAndUpload} 
            disabled={!file || uploading}
          >
            {uploading ? "Uploading..." : "Upload Questions"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default BulkUploadModal;